import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  useITopFiveApiV2EndpointsTopFiveAddOrUpdateTopFiveAddOrUpdateTopFiveEndpoint,
  getITopFiveApiV2EndpointsTopFiveGetTopFiveGetTopFiveEndpointQueryKey,
} from '@/api/api.generated';
import type {
  ITopFiveApiV2EndpointsTopFiveAddOrUpdateTopFiveAddOrUpdateTopFiveRequest,
  ITopFiveApiV2EndpointsTopFiveAddOrUpdateTopFiveAddOrUpdateTopFiveResponse,
} from '@/api/models';
import { AxiosError } from 'axios';

export interface AddOrUpdateTopFiveVariables {
  staffId: number;
  request: ITopFiveApiV2EndpointsTopFiveAddOrUpdateTopFiveAddOrUpdateTopFiveRequest;
}

export interface UseAddOrUpdateTopFiveResult {
  addOrUpdate: (variables: AddOrUpdateTopFiveVariables) => Promise<ITopFiveApiV2EndpointsTopFiveAddOrUpdateTopFiveAddOrUpdateTopFiveResponse>;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: AxiosError | null;
  reset: () => void;
}

/**
 * Custom hook for adding or updating TopFive for a specific staff member.
 * 
 * Features:
 * - Creates new TopFive if doesn't exist for the date range
 * - Updates existing TopFive if TopFiveId or date range matches
 * - Automatically invalidates related queries on success
 * 
 * @example
 * ```tsx
 * const { addOrUpdate, isLoading, isSuccess, error } = useAddOrUpdateTopFive();
 * 
 * const handleSubmit = async () => {
 *   await addOrUpdate({
 *     staffId: 123,
 *     request: {
 *       topFiveId: null, // Or existing TopFiveId for updates
 *       staffId: 123,
 *       startDate: new Date('2025-01-01'),
 *       endDate: new Date('2025-04-30'),
 *       items: [
 *         { topFiveItemId: null, name: 'Goal 1', weight: 20, completion: 0, status: 'Green' },
 *         { topFiveItemId: null, name: 'Goal 2', weight: 30, completion: 0, status: null },
 *       ]
 *     }
 *   });
 * };
 * ```
 */
export const useAddOrUpdateTopFive = (): UseAddOrUpdateTopFiveResult => {
  const queryClient = useQueryClient();

  const mutation = useITopFiveApiV2EndpointsTopFiveAddOrUpdateTopFiveAddOrUpdateTopFiveEndpoint({
    mutation: {
      onSuccess: async () => {
        // Invalidate TopFive list query to refetch updated data
        const topFiveQueryKey = getITopFiveApiV2EndpointsTopFiveGetTopFiveGetTopFiveEndpointQueryKey();
        await queryClient.invalidateQueries({ queryKey: topFiveQueryKey });
      },
    },
    axios: {
      withCredentials: true,
    },
  });

  const addOrUpdate = async (variables: AddOrUpdateTopFiveVariables): Promise<ITopFiveApiV2EndpointsTopFiveAddOrUpdateTopFiveAddOrUpdateTopFiveResponse> => {
    const response = await mutation.mutateAsync({
      staffId: variables.staffId,
      data: variables.request,
    });
    return response.data;
  };

  return {
    addOrUpdate,
    isLoading: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error as AxiosError | null,
    reset: mutation.reset,
  };
};
